import { motion } from "framer-motion";
import { isEmpty } from "lodash";
import { classNames } from "primereact/utils";
import { Ref } from "react";
import { useTranslation } from "react-i18next";
import { setTruncatedTitle } from "../../../../../common/utils";
import type { PanelTreeNode } from "./PanelTemplate";

interface CompanyOptionTemplateProps {
    company: PanelTreeNode;
    isSelected: boolean;
    ref: Ref<HTMLDivElement> | null;
    onSelect: (companyId: string) => void;
    onToggleExpand: (companyId: string) => void;
    isSingleCompany: boolean;
}

const CompanyOptionTemplate = (props: CompanyOptionTemplateProps) => {
    const {
        company,
        isSelected,
        ref,
        onSelect,
        onToggleExpand,
        isSingleCompany,
    } = props;

    const { t } = useTranslation("common");

    return (
        <div
            ref={ref}
            className={classNames(
                "flex items-center gap-2.5 rounded-md px-2.5 py-2 cursor-pointer hover:bg-surface-515 transition-colors duration-150",
                {
                    "bg-surface-515":
                        !isSingleCompany &&
                        !isSelected &&
                        company.isExpanded,
                    "bg-tertiary-50!": isSelected,
                }
            )}
            onClick={() => onSelect(company.id)}
        >
            <div
                className={classNames(
                    "flex size-6.5 shrink-0 items-center justify-center rounded-md",
                    isSelected
                        ? "bg-tertiary-600 text-tertiary-text"
                        : "bg-tertiary-50 text-tertiary-600"
                )}
            >
                <i className="pi pi-building text-xs" />
            </div>
            <div className="flex min-w-0 grow flex-col">
                <span
                    className={classNames(
                        "truncate",
                        isSelected
                            ? "font-semibold text-tertiary-700"
                            : "font-bold"
                    )}
                    onMouseEnter={setTruncatedTitle}
                >
                    {company.name}
                </span>
                <span className="truncate text-xs text-text-300">
                    {isSingleCompany
                        ? `${t("common:allLocations")} (${company.children?.length ?? 0})`
                        : t("common:locationsCount", {
                              count: company.children?.length ?? 0,
                          })}
                </span>
            </div>
            {!isSingleCompany && !isEmpty(company.children) && (
                <>
                    <div className="mx-1 h-4 w-px shrink-0 bg-surface-545" />
                    <button
                        type="button"
                        aria-label="expand"
                        className={classNames(
                            "flex size-9.5 md:size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-325 hover:text-text-500 transition-colors duration-150",
                            isSelected
                                ? "hover:bg-tertiary-100"
                                : "hover:bg-surface-535",
                            {
                                "bg-tertiary-100":
                                    company.isExpanded && isSelected,
                                "bg-surface-535":
                                    company.isExpanded && !isSelected,
                            }
                        )}
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggleExpand(company.id);
                        }}
                    >
                        <motion.i
                            className="pi pi-chevron-right text-sm"
                            animate={{ rotate: company.isExpanded ? 90 : 0 }}
                            transition={{ duration: 0.1, ease: "easeInOut" }}
                            initial={false}
                        />
                    </button>
                </>
            )}
        </div>
    );
};

export default CompanyOptionTemplate;
